import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AnimatePresence, motion } from 'framer-motion';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { IoMenu, IoClose } from 'react-icons/io5';

import stateConfig from '../../stateConfig';
import { logOut } from '../../features/user/userSlice';

const NAV_ITEMS = [
  { id: 'search', label: 'AI Search', match: ['search'] },
  { id: 'videos', label: 'Video Transcripts', match: ['videos', 'video'] },
  { id: 'lobby', label: 'Lobby', match: ['lobby', 'lobbyist', 'company'] },
];

const MainHeader = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = useParams();
  const [mobileOpen, setMobileOpen] = useState(false);
  const { features, redirectUrl, auth } = useSelector((s) => s.user);

  const config = stateConfig[state?.toLowerCase()] || {};
  const isBlueHeader = !!config.bannerBlue;
  const segments = location.pathname.split('/').filter(Boolean);
  const activeSegment = segments[1] || '';

  const isActive = (item) => item.match.includes(activeSegment);

  const handleLogOut = () => {
    dispatch(logOut());
    navigate('/');
  };

  const visibleItems = NAV_ITEMS.filter((item) => features?.includes(item.id));

  const headerBg = isBlueHeader
    ? 'bg-darkblue text-white border-b border-darkblue/40'
    : 'bg-white/95 text-slate-700 border-b border-slate-200 backdrop-blur supports-[backdrop-filter]:bg-white/80';

  const linkBase = 'relative rounded-md px-3 py-2 text-sm font-medium transition-colors';
  const linkInactive = isBlueHeader
    ? 'text-white/80 hover:text-white hover:bg-white/10'
    : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50';
  const linkActive = isBlueHeader
    ? 'text-white bg-white/15'
    : 'text-slate-900 bg-slate-100';
  const indicatorBg = isBlueHeader ? 'bg-lightblue' : 'bg-blue-600';

  const logoutBtn = isBlueHeader
    ? 'border border-white/40 hover:bg-white/15 text-white'
    : 'border border-slate-200 hover:bg-slate-50 text-slate-700';

  const menuBtn = isBlueHeader
    ? 'border border-white/40 bg-white/10 text-white hover:bg-white/20'
    : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50';

  return (
    <header className={`fixed z-30 top-0 inset-x-0 h-[75px] ${headerBg} print:hidden`}>
      <div className="flex h-full w-full items-center justify-between px-6 sm:px-8 xl:px-10">
        <a href={redirectUrl} target="_top" className="flex items-center" aria-label="Home">
          {config.logo ? (
            <svg width="200" height="48" viewBox="0 0 188 41" className="h-11 w-auto">
              <image href={config.logo} width="188" height="41" />
            </svg>
          ) : (
            <span
              className={`text-lg font-semibold tracking-tight ${
                isBlueHeader ? 'text-white' : 'text-slate-900'
              }`}
            >
              Telicon
            </span>
          )}
        </a>

        <div className="flex items-center gap-2">
          <nav className="hidden xl:flex items-center gap-1">
            {visibleItems.map((item) => {
              const active = isActive(item);
              return (
                <Link
                  key={item.id}
                  to={`/${state}/${item.id}`}
                  className={`${linkBase} ${active ? linkActive : linkInactive}`}
                >
                  {item.label}
                  {active && (
                    <span
                      aria-hidden="true"
                      className={`absolute inset-x-3 -bottom-[1px] h-0.5 rounded-full ${indicatorBg}`}
                    />
                  )}
                </Link>
              );
            })}
            {auth === 'login' && (
              <button
                type="button"
                onClick={handleLogOut}
                className={`ml-3 inline-flex h-9 items-center rounded-md px-3 text-sm font-medium transition-colors ${logoutBtn}`}
              >
                Log out
              </button>
            )}
          </nav>

          <div className="relative xl:hidden">
            <button
              type="button"
              aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={mobileOpen}
              onClick={() => setMobileOpen((prev) => !prev)}
              className={`inline-flex h-9 w-9 items-center justify-center rounded-md ${menuBtn}`}
            >
              {mobileOpen ? <IoClose className="h-5 w-5" /> : <IoMenu className="h-5 w-5" />}
            </button>
            <AnimatePresence>
              {mobileOpen && (
                <motion.div
                  key="mobile-menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  className="absolute right-0 top-12 z-40 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg shadow-slate-300/30"
                >
                  <ul className="flex flex-col py-2">
                    {visibleItems.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/${state}/${item.id}`}
                          onClick={() => setMobileOpen(false)}
                          className={`block px-4 py-2 text-sm transition-colors ${
                            isActive(item)
                              ? 'bg-slate-50 font-medium text-slate-900'
                              : 'text-slate-700 hover:bg-slate-50'
                          }`}
                        >
                          {item.label}
                        </Link>
                      </li>
                    ))}
                    {auth === 'login' && (
                      <li className="border-t border-slate-100 mt-2 pt-2 px-2 pb-2">
                        <button
                          type="button"
                          onClick={() => {
                            setMobileOpen(false);
                            handleLogOut();
                          }}
                          className="w-full inline-flex h-9 items-center justify-center rounded-md border border-slate-200 px-3 text-sm font-medium text-slate-700 hover:bg-slate-50"
                        >
                          Log out
                        </button>
                      </li>
                    )}
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </header>
  );
};

export default MainHeader;
